import type { ColumnFormat } from './columns';

const ONE_DECIMAL_PLACE = 1;
const TWO_DECIMAL_PLACES = 2;
const PERCENT_MULTIPLIER = 100;

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

/**
 * Format a cell value for display. The format hint comes from the column
 * definition; aggregation and per-million toggles influence integer-vs-decimal
 * rendering for the default `number` formatter.
 */
export const formatCell = (
  value: unknown,
  format: ColumnFormat | undefined,
  aggregation: 'total' | 'perAppearance' | 'perStart' | 'per90',
  perMillion: boolean,
  price: number | null
): string => {
  const numeric = asNumber(value);
  if (numeric === null) return '-';

  if (format === 'percent') {
    const ratio = numeric > 1 ? numeric / PERCENT_MULTIPLIER : numeric;
    return `${Math.round(ratio * PERCENT_MULTIPLIER)}%`;
  }

  if (format === 'price') {
    return `£${numeric.toFixed(ONE_DECIMAL_PLACE)}m`;
  }

  if (format === 'number') {
    return numeric.toFixed(TWO_DECIMAL_PLACES);
  }

  // Default — `integer` and unspecified: integer for total aggregation, 2 d.p.
  // otherwise. Per-million scales the value by the player's price so that
  // "stat per million spent" lines up across cost tiers.
  const scaled =
    perMillion && price !== null && price > 0 ? numeric / price : numeric;

  if (aggregation === 'total' && !perMillion) {
    return Math.round(scaled).toString();
  }
  return scaled.toFixed(TWO_DECIMAL_PLACES);
};
